import type { SoloRiddle } from './SoloRiddleService'

type RiddleLocale = 'ru' | 'en'

interface RiddleSnapshot {
  locale: RiddleLocale
  version: string
  updatedAt: number
  riddles: SoloRiddle[]
}

const DATABASE_NAME = 'oneword-offline'
const DATABASE_VERSION = 1
const STORE_NAME = 'riddle-snapshots'
const FALLBACK_PREFIX = 'offline-riddles:'

class OfflineRiddleStore {
  private databasePromise: Promise<IDBDatabase> | null = null

  private openDatabase() {
    if (this.databasePromise) return this.databasePromise

    this.databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: 'locale' })
        }
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })

    return this.databasePromise
  }

  private fallbackKey(locale: RiddleLocale) {
    return `${FALLBACK_PREFIX}${locale}`
  }

  private readFallback(locale: RiddleLocale) {
    try {
      const value = localStorage.getItem(this.fallbackKey(locale))
      return value ? JSON.parse(value) as RiddleSnapshot : null
    }
    catch {
      return null
    }
  }

  private writeFallback(snapshot: RiddleSnapshot) {
    try {
      localStorage.setItem(this.fallbackKey(snapshot.locale), JSON.stringify(snapshot))
    }
    catch {
      // IndexedDB remains the primary store. Storage pressure should not stop
      // a currently running solo game.
    }
  }

  async get(locale: RiddleLocale): Promise<RiddleSnapshot | null> {
    if (!('indexedDB' in window)) return this.readFallback(locale)

    try {
      const database = await this.openDatabase()
      return await new Promise((resolve, reject) => {
        const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(locale)
        request.onsuccess = () => resolve((request.result as RiddleSnapshot | undefined) || this.readFallback(locale))
        request.onerror = () => reject(request.error)
      })
    }
    catch {
      return this.readFallback(locale)
    }
  }

  async set(snapshot: RiddleSnapshot) {
    this.writeFallback(snapshot)
    if (!('indexedDB' in window)) return

    try {
      const database = await this.openDatabase()
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, 'readwrite')
        transaction.objectStore(STORE_NAME).put(snapshot)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(transaction.error)
      })
    }
    catch {
      // localStorage fallback was written first.
    }
  }
}

const offlineRiddleStore = new OfflineRiddleStore()

export { offlineRiddleStore, RiddleLocale, RiddleSnapshot }
